import {
  BOM_FIELDS,
  DEFAULT_BOM,
  DRIVE_MASS_TARGET,
  ROBOT_MASS_TARGET,
  SET_AXIS_COUNT,
  SET_MOTOR_MASS,
} from '@/domain/hs1'
import { Alert, Button, Card } from '@/components/ui'
import { useSimulation } from '@/sim/SimulationProvider'
import { Metric, num } from '@/sim/parts'

/**
 * Die Annahmen der Stücklistenrechnung — jetzt bei den Eingaben.
 *
 * SIE STANDEN AUF DER STÜCKLISTE, weil dort gerechnet wird. Das war die
 * Begründung der Rechnung und nicht die der Bedienung: wer eine Annahme sucht,
 * sucht sie bei den Parametern, zusammen mit Kadenz, Körpermasse und Zuladung.
 * Die Stückliste zeigt jetzt nur noch, was daraus folgt.
 *
 * WAS SIE SIND UND WAS NICHT. Jede dieser sechs Zahlen ist eine Behauptung über
 * Technik, die es noch nicht gibt — kein Messwert und kein Katalogwert. Sie
 * stehen deshalb hinter einem Hinweis und nicht kommentarlos neben den
 * gemessenen Grössen.
 *
 * DIE MOMENTENDICHTE HAT AN GEWICHT VERLOREN, und das ist eine gute
 * Nachricht: seit die Motorisierung des Gen5-Stands gesetzt ist, hängen
 * dreiundzwanzig der fünfundzwanzig Achsen nicht mehr an ihr. Sie wirkt nur
 * noch dort, wo nichts entschieden ist. Genau das sagt die Karte auch.
 */
export function Assumptions() {
  const { bom, setBom } = useSimulation()

  const veraendert = BOM_FIELDS.filter((f) => bom[f.key] !== DEFAULT_BOM[f.key])

  return (
    <div className="stack">
      <Card
        title="Annahmen der Stücklistenrechnung"
        eyebrow={
          veraendert.length > 0
            ? `${veraendert.length} von ${BOM_FIELDS.length} verstellt`
            : 'alle auf Ausgangswert'
        }
        actions={
          veraendert.length > 0 ? (
            <Button size="sm" variant="ghost" onClick={() => setBom(DEFAULT_BOM)}>
              Zurücksetzen
            </Button>
          ) : undefined
        }
      >
        <Alert tone="warning" title="Keine dieser Zahlen ist gemessen">
          Sie sind Grössenordnungen aus Katalogen und Erfahrung. Die Stückliste rechnet mit
          ihnen bis zum Fixpunkt — was dabei herauskommt, ist so belastbar wie die
          schwächste dieser sechs Annahmen.
        </Alert>

        <div className="workbench">
          {BOM_FIELDS.map((field) => (
            <label key={field.key} className="slider">
              <span className="slider__label">{field.label}</span>
              <span className="slider__value">
                {num(bom[field.key], field.digits)}
                {field.unit ? ` ${field.unit}` : ''}
                {bom[field.key] !== DEFAULT_BOM[field.key] && (
                  <span className="cell-muted">
                    {' '}
                    statt {num(DEFAULT_BOM[field.key], field.digits)}
                  </span>
                )}
              </span>
              <input
                className="slider__input"
                type="range"
                min={field.min}
                max={field.max}
                step={field.step}
                value={bom[field.key]}
                onChange={(event) => setBom({ [field.key]: Number(event.target.value) })}
              />
            </label>
          ))}
        </div>
      </Card>

      <Card title="Zielmassen" eyebrow="Vorgabe, keine Rechnung">
        <div className="metrics">
          <Metric
            label="Antriebe"
            value={`${DRIVE_MASS_TARGET} kg`}
            note={`davon ${num(SET_MOTOR_MASS, 1)} kg Motoren aus der gesetzten Auswahl`}
          />
          <Metric
            label="Ganze Maschine"
            value={`${ROBOT_MASS_TARGET} kg`}
            note="Zielmasse des Demonstrators"
          />
          <Metric
            label="Rest je Achse"
            value={`${num(((DRIVE_MASS_TARGET - SET_MOTOR_MASS) / SET_AXIS_COUNT) * 1000, 0)} g`}
            note={`Getriebe, Gehäuse, Lager und Geber — auf ${SET_AXIS_COUNT} Achsen`}
            warn
          />
        </div>
        <p className="cell-muted" style={{ marginTop: 'var(--space-3)' }}>
          Die Zielmassen stehen fest, die Momentendichte darüber wirkt nur noch auf die drei
          Achsen ohne getroffene Auswahl: Seitneigung der Wirbelsäule und die beiden
          Halsachsen. Alles andere hängt an der Motorenliste.
        </p>
      </Card>
    </div>
  )
}
